import { Button, HStack, IconButton, Input } from "@chakra-ui/react";
import React, { forwardRef, memo, useCallback } from "react";
import colors from "app/colors";
import { getTaskDueDate } from "modules/utils/Functions";
import moment from "moment";
import { TaskDueDateOption } from "modules/utils/Constants";
import { CloseIcon } from "modules/icons";

interface IProps {
  dueTime?: string | null;
  taskDueDateOption?: TaskDueDateOption;
  onDateChanged: (
    taskDueDateOption: TaskDueDateOption,
    dueTime: string | null,
  ) => void;
  onDateCleared: () => void;
}

const DueDateSelector: React.FC<IProps> = ({
  dueTime,
  taskDueDateOption,
  onDateChanged,
  onDateCleared,
}: IProps) => {
  const onDueDateOptionClicked = useCallback(
    (option: TaskDueDateOption) => {
      onDateChanged(option, getTaskDueDate(option));
    },
    [onDateChanged],
  );

  const onCustomDateTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const dateTimeString = e.target.value;
      const dueTime = dateTimeString
        ? moment(dateTimeString).endOf("day").toISOString()
        : null;
      onDateChanged("Custom", dueTime);
    },
    [onDateChanged],
  );

  return (
    <HStack w="full">
      <DueDateButton
        title="Today"
        option="Today"
        isSelected={taskDueDateOption === "Today"}
        onSelected={onDueDateOptionClicked}
        onCleared={onDateCleared}
      />
      <DueDateButton
        title="Tomorrow"
        option="Tomorrow"
        isSelected={taskDueDateOption === "Tomorrow"}
        onSelected={onDueDateOptionClicked}
        onCleared={onDateCleared}
      />
      <DueDateButton
        title="1 Week"
        option="1 Week"
        isSelected={taskDueDateOption === "1 Week"}
        onSelected={onDueDateOptionClicked}
        onCleared={onDateCleared}
      />
      <HStack
        spacing={0}
        borderWidth={1}
        borderColor={colors.gray0}
        borderRadius="full"
      >
        <Input
          placeholder="Select Date"
          size="md"
          type="date"
          w={135}
          onChange={onCustomDateTimeChange}
          value={dueTime ? moment(dueTime).format("YYYY-MM-DD") : ""}
          h="fit-content"
          border="none"
          pr={0}
        />
        <IconButton
          variant="hover"
          size="xs"
          icon={<CloseIcon boxSize={4} fill={colors.gray30} />}
          onClick={onDateCleared}
          aria-label="clear due date"
        />
      </HStack>
    </HStack>
  );
};

interface DueDateButtonProps {
  title: string;
  option: TaskDueDateOption;
  isSelected: boolean;
  onSelected: (option: TaskDueDateOption) => void;
  onCleared: () => void;
  displayRemoveIcon?: boolean;
}

export const DueDateButton = forwardRef<HTMLButtonElement, DueDateButtonProps>(
  (
    {
      title,
      option,
      isSelected,
      onSelected,
      onCleared,
      displayRemoveIcon = false,
    },
    ref,
  ) => {
    const color = isSelected ? colors.blue : colors.grayMain;
    const fontWeight = isSelected ? 700 : 500;
    const bgColor = isSelected ? colors.blueLight : colors.white;

    const _onClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (isSelected) {
          onCleared();
          return;
        }
        onSelected(option);
      },
      [isSelected, onCleared, onSelected, option],
    );

    return (
      <Button
        ref={ref}
        variant="outline"
        rounded="full"
        fontSize={14}
        color={color}
        fontWeight={fontWeight}
        bg={bgColor}
        size="xs"
        onClick={_onClick}
        rightIcon={
          isSelected && displayRemoveIcon ? (
            <CloseIcon boxSize={4} fill={colors.blue} />
          ) : undefined
        }
      >
        {title}
      </Button>
    );
  },
);

export default memo(DueDateSelector);
